import type { Section, SectionInput, SectionKind } from '../lib/api'

/**
 * The editable shape of a section, as a form holds it.
 *
 * maxFiles and accept are strings rather than `number | null` and `string[]`
 * because these are raw <input> values: a half-typed "1" on the way to "12", or
 * the trailing comma in "image/*, " that has to survive the next keystroke.
 * Coercion happens once, in draftToInput, on the way to the wire.
 */
export interface SectionDraft {
  kind: SectionKind
  title: string
  description: string
  required: boolean
  maxFiles: string
  accept: string
}

export const emptyDraft = (kind: SectionKind): SectionDraft => ({
  kind,
  title: '',
  description: '',
  required: true,
  maxFiles: '',
  accept: '',
})

/** Persisted row to form draft. The inverse of draftToInput. */
export const draftFrom = (section: Section): SectionDraft => ({
  kind: section.kind,
  title: section.title,
  description: section.description,
  required: section.required,
  maxFiles: section.maxFiles === null ? '' : String(section.maxFiles),
  accept: section.accept.join(', '),
})

/**
 * Form draft to wire body.
 *
 * Shared by the builder at /new and the admin editor so the two cannot disagree
 * about what an empty field means — that is the whole reason this lives beside
 * the inputs rather than inside either caller. maxFiles and accept are dropped
 * on a text section because the server refuses a cap there outright.
 */
export function draftToInput(draft: SectionDraft): SectionInput {
  const capped = draft.kind === 'files' && draft.maxFiles.trim() !== ''
  const n = Number(draft.maxFiles.trim())
  return {
    kind: draft.kind,
    title: draft.title.trim(),
    description: draft.description.trim(),
    required: draft.required,
    // Number.isFinite guards a pasted non-numeric value, which would otherwise
    // reach JSON.stringify as NaN and silently become null.
    maxFiles: capped && Number.isFinite(n) ? n : null,
    accept:
      draft.kind === 'files' && draft.accept.trim() !== ''
        ? draft.accept
            .split(',')
            .map((a) => a.trim())
            .filter(Boolean)
        : [],
  }
}

interface Props {
  value: SectionDraft
  /** A partial patch, matching how both callers already track their drafts. */
  onChange: (changes: Partial<SectionDraft>) => void
  /** Set while a save is in flight. */
  disabled?: boolean
  /**
   * Prefix for the input ids, so <label for> binds. Several of these render on
   * one page, so the ids cannot be constants.
   */
  idPrefix: string
}

/**
 * The fields of a section, and nothing else — no card, no heading, no reorder
 * or remove controls.
 *
 * That line is where the two callers genuinely differ. /new edits an unsaved
 * array, so its Remove is a splice and its ↑/↓ are local; the admin page edits
 * persisted rows, so the same three buttons are network calls with their own
 * confirmations and errors. Sharing that chrome would need a prop for every one
 * of those differences. The inputs, by contrast, are a direct rendering of the
 * row and will stay identical, so they are shared and every future field is
 * added once.
 */
export function SectionFields({ value, onChange, disabled, idPrefix }: Props) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="label" htmlFor={`${idPrefix}-title`}>
            Title
          </label>
          <input
            id={`${idPrefix}-title`}
            required
            disabled={disabled}
            className="input"
            value={value.title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder={value.kind === 'files' ? 'Signed contracts' : 'Notes for the reviewer'}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label" htmlFor={`${idPrefix}-description`}>
            Description (optional)
          </label>
          <input
            id={`${idPrefix}-description`}
            disabled={disabled}
            className="input"
            value={value.description}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </div>

        {value.kind === 'files' && (
          <>
            <div>
              <label className="label" htmlFor={`${idPrefix}-max-files`}>
                Max files (optional)
              </label>
              <input
                id={`${idPrefix}-max-files`}
                type="number"
                min={1}
                disabled={disabled}
                className="input"
                value={value.maxFiles}
                onChange={(e) => onChange({ maxFiles: e.target.value })}
                placeholder="unlimited"
              />
            </div>
            <div>
              <label className="label" htmlFor={`${idPrefix}-accept`}>
                Accepted types (optional)
              </label>
              <input
                id={`${idPrefix}-accept`}
                disabled={disabled}
                className="input"
                value={value.accept}
                onChange={(e) => onChange({ accept: e.target.value })}
                placeholder="application/pdf, image/*, .zip"
              />
            </div>
          </>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          disabled={disabled}
          checked={value.required}
          onChange={(e) => onChange({ required: e.target.checked })}
          className="size-4 rounded border-zinc-300 accent-brand-600"
        />
        Required — the recipient cannot submit without it
      </label>
    </>
  )
}
